import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { apiClient } from '../../api/client';
import { useAuth } from '../../context/authContext';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import {
  addFeeStructureItem,
  createFeeStructure,
  duplicateFeeStructure,
  FeeStructure,
  FeeStructureItem,
  getFeeStructureUsage,
  listFeeStructures,
  setFeeStructureItemPublished,
  setFeeStructureStatus,
  updateFeeStructureItem,
} from '../../api/feeStructures';

interface CountryOption {
  id: number;
  name: string;
}
interface UniversityOption {
  id: number;
  name: string;
  countryId: number;
  isActive?: boolean;
}
interface CourseOption {
  id: number;
  name: string;
}
interface IntakeOption {
  id: number;
  name: string;
}
interface PaymentCategoryOption {
  id: number;
  name: string;
}
interface CurrencyOption {
  id: number;
  code: string;
}

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) return err.response?.data?.message ?? 'Something went wrong.';
  return 'Something went wrong.';
};

const STATUS_COLOR: Record<FeeStructure['status'], 'default' | 'success' | 'warning'> = {
  DRAFT: 'warning',
  PUBLISHED: 'success',
  ARCHIVED: 'default',
};

export const FeeStructuresPage = () => {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('master_data', 'update');
  const canCreate = hasPermission('master_data', 'create');

  const [countryId, setCountryId] = useState<number | ''>('');
  const [universityId, setUniversityId] = useState<number | ''>('');
  const [courseId, setCourseId] = useState<number | ''>('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [newIntakeId, setNewIntakeId] = useState<number | ''>('');
  const [newLabel, setNewLabel] = useState('');

  const [duplicateTarget, setDuplicateTarget] = useState<FeeStructure | null>(null);
  const [duplicateIntakeId, setDuplicateIntakeId] = useState<number | ''>('');

  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editItem, setEditItem] = useState<FeeStructureItem | null>(null);

  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: async () => (await apiClient.get('/master-data/countries')).data.data as CountryOption[],
  });
  const universitiesQuery = useQuery({
    queryKey: ['lookup', '/master-data/universities', countryId],
    queryFn: async () =>
      (await apiClient.get('/master-data/universities', { params: { countryId } })).data.data as UniversityOption[],
    enabled: countryId !== '',
  });
  const coursesQuery = useQuery({
    queryKey: ['lookup', '/master-data/courses', universityId],
    queryFn: async () =>
      (await apiClient.get('/master-data/courses', { params: { universityId } })).data.data as CourseOption[],
    enabled: universityId !== '',
  });
  const intakesQuery = useQuery({
    queryKey: ['lookup', '/master-data/intakes'],
    queryFn: async () => (await apiClient.get('/master-data/intakes')).data.data as IntakeOption[],
  });
  const paymentCategoriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/payment-categories'],
    queryFn: async () =>
      (await apiClient.get('/master-data/payment-categories', { params: { activeOnly: true } })).data
        .data as PaymentCategoryOption[],
  });
  const currenciesQuery = useQuery({
    queryKey: ['lookup', '/master-data/currencies'],
    queryFn: async () =>
      (await apiClient.get('/master-data/currencies', { params: { activeOnly: true } })).data.data as CurrencyOption[],
  });

  const structuresQuery = useQuery({
    queryKey: ['fee-structures', universityId, courseId],
    queryFn: () => listFeeStructures({ universityId: universityId || undefined, courseId: courseId || undefined }),
    enabled: universityId !== '',
  });
  const selected = structuresQuery.data?.find((s) => s.id === selectedId) ?? null;

  const usageQuery = useQuery({
    queryKey: ['fee-structure-usage', duplicateTarget?.id],
    queryFn: () => getFeeStructureUsage(duplicateTarget!.id),
    enabled: !!duplicateTarget,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['fee-structures'] });

  const createMutation = useMutation({
    mutationFn: () =>
      createFeeStructure({
        universityId: universityId as number,
        courseId: courseId || undefined,
        intakeId: newIntakeId || undefined,
        label: newLabel || undefined,
      }),
    onSuccess: (created) => {
      invalidate();
      setAddOpen(false);
      setNewIntakeId('');
      setNewLabel('');
      setSelectedId(created.id);
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'publish' | 'unpublish' | 'archive' }) =>
      setFeeStructureStatus(id, action),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const duplicateMutation = useMutation({
    mutationFn: () => duplicateFeeStructure(duplicateTarget!.id, duplicateIntakeId || undefined),
    onSuccess: (created) => {
      invalidate();
      setDuplicateTarget(null);
      setDuplicateIntakeId('');
      setSelectedId(created.id);
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const structureColumns: GridColDef<FeeStructure>[] = [
    { field: 'version', headerName: 'Version', width: 90 },
    { field: 'label', headerName: 'Label', flex: 1, valueGetter: (_v, row) => row.label ?? '—' },
    { field: 'course', headerName: 'Program', width: 160, valueGetter: (_v, row) => row.course?.name ?? 'University-wide' },
    { field: 'intake', headerName: 'Intake', width: 160, valueGetter: (_v, row) => row.intake?.name ?? '—' },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => <Chip size="small" label={params.value} color={STATUS_COLOR[params.value as FeeStructure['status']]} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 260,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          {canManage && params.row.status === 'DRAFT' && (
            <Button size="small" onClick={() => statusMutation.mutate({ id: params.row.id, action: 'publish' })}>
              Publish
            </Button>
          )}
          {canManage && params.row.status === 'PUBLISHED' && (
            <Button size="small" onClick={() => statusMutation.mutate({ id: params.row.id, action: 'unpublish' })}>
              Unpublish
            </Button>
          )}
          {canManage && params.row.status !== 'ARCHIVED' && (
            <Button size="small" color="error" onClick={() => statusMutation.mutate({ id: params.row.id, action: 'archive' })}>
              Archive
            </Button>
          )}
          {canCreate && (
            <Button
              size="small"
              onClick={() => {
                setError(null);
                setDuplicateTarget(params.row);
              }}
            >
              Duplicate
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={PaymentsOutlinedIcon}
        color="#12B76A"
        title="Payment & Fee Structures"
        subtitle="Configure university/program/intake-specific payment checklists — never one global amount for every university."
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <TextField
          select
          label="Country"
          size="small"
          sx={{ width: 180 }}
          value={countryId}
          onChange={(e) => {
            setCountryId(Number(e.target.value));
            setUniversityId('');
            setCourseId('');
            setSelectedId(null);
          }}
        >
          {countriesQuery.data?.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="University"
          size="small"
          sx={{ width: 220 }}
          disabled={countryId === ''}
          value={universityId}
          onChange={(e) => {
            setUniversityId(Number(e.target.value));
            setCourseId('');
            setSelectedId(null);
          }}
        >
          {universitiesQuery.data
            ?.filter((u) => u.isActive !== false || u.id === universityId)
            .map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
                {u.isActive === false ? ' (inactive)' : ''}
              </MenuItem>
            ))}
        </TextField>
        <TextField
          select
          label="Program (optional)"
          size="small"
          sx={{ width: 220 }}
          disabled={universityId === ''}
          value={courseId}
          onChange={(e) => {
            setCourseId(e.target.value ? Number(e.target.value) : '');
            setSelectedId(null);
          }}
        >
          <MenuItem value="">
            <em>University-wide only</em>
          </MenuItem>
          {coursesQuery.data?.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        {canCreate && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            disabled={universityId === ''}
            onClick={() => {
              setError(null);
              setAddOpen(true);
            }}
          >
            New fee structure
          </Button>
        )}
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Fee Structures {universityId ? '' : '(select a university)'}
          </Typography>
          <Box sx={{ height: 420, bgcolor: 'background.paper', borderRadius: 1 }}>
            <DataGrid
              rows={structuresQuery.data ?? []}
              columns={structureColumns}
              loading={structuresQuery.isFetching}
              slots={{ toolbar: TableToolbar }}
              onRowClick={(params) => setSelectedId(params.row.id)}
              disableRowSelectionOnClick={false}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Payment Checklist {selected ? `— ${selected.university.name}${selected.course ? ` / ${selected.course.name}` : ''}` : '(select a fee structure)'}
          </Typography>
          {selected && (
            <FeeStructureItemsPanel
              structure={selected}
              canManage={canManage}
              canCreate={canCreate}
              paymentCategories={paymentCategoriesQuery.data ?? []}
              currencies={currenciesQuery.data ?? []}
              addItemOpen={addItemOpen}
              setAddItemOpen={setAddItemOpen}
              editItem={editItem}
              setEditItem={setEditItem}
              onError={setError}
            />
          )}
        </Grid>
      </Grid>

      {/* Add Fee Structure */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New fee structure</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              select
              label="Intake (optional — leave blank for a university-wide default)"
              fullWidth
              value={newIntakeId}
              onChange={(e) => setNewIntakeId(e.target.value ? Number(e.target.value) : '')}
            >
              <MenuItem value="">
                <em>Not intake-specific</em>
              </MenuItem>
              {intakesQuery.data?.map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Label (e.g. GSMU-2027-V1)" fullWidth value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>
            Create (Draft)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Fee Structure */}
      <Dialog open={!!duplicateTarget} onClose={() => setDuplicateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Duplicate fee structure</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="body2">
              {duplicateTarget?.label ?? duplicateTarget?.version} is used by{' '}
              {usageQuery.isLoading ? '…' : `${usageQuery.data?.applications ?? 0} application(s)`}. Duplicating creates a
              new DRAFT version with every item copied — edit it freely, then publish when ready (e.g. for the next
              intake).
            </Typography>
            <TextField
              select
              label="Intake for the new version (optional)"
              fullWidth
              value={duplicateIntakeId}
              onChange={(e) => setDuplicateIntakeId(e.target.value ? Number(e.target.value) : '')}
            >
              <MenuItem value="">
                <em>Keep the same intake</em>
              </MenuItem>
              {intakesQuery.data?.map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateTarget(null)}>Cancel</Button>
          <Button variant="contained" disabled={duplicateMutation.isPending} onClick={() => duplicateMutation.mutate()}>
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface FeeStructureItemsPanelProps {
  structure: FeeStructure;
  canManage: boolean;
  canCreate: boolean;
  paymentCategories: PaymentCategoryOption[];
  currencies: CurrencyOption[];
  addItemOpen: boolean;
  setAddItemOpen: (v: boolean) => void;
  editItem: FeeStructureItem | null;
  setEditItem: (item: FeeStructureItem | null) => void;
  onError: (msg: string | null) => void;
}

const FeeStructureItemsPanel = ({
  structure,
  canManage,
  canCreate,
  paymentCategories,
  currencies,
  addItemOpen,
  setAddItemOpen,
  editItem,
  setEditItem,
  onError,
}: FeeStructureItemsPanelProps) => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['fee-structures'] });

  const publishItemMutation = useMutation({
    mutationFn: ({ itemId, published }: { itemId: number; published: boolean }) =>
      setFeeStructureItemPublished(structure.id, itemId, published),
    onSuccess: () => {
      invalidate();
      onError(null);
    },
    onError: (err) => onError(extractErrorMessage(err)),
  });

  const itemColumns: GridColDef<FeeStructureItem>[] = [
    { field: 'paymentCategory', headerName: 'Payment Item', flex: 1, valueGetter: (_v, row) => row.paymentCategory.name },
    { field: 'isRequired', headerName: 'Required', width: 90, valueGetter: (_v, row) => (row.isRequired ? 'Yes' : 'No') },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 140,
      valueGetter: (_v, row) =>
        row.amount ?? (row.semester1Amount && row.semester2Amount ? `${row.semester1Amount}+${row.semester2Amount}` : row.firstYearAmount ?? '—'),
    },
    { field: 'currency', headerName: 'Currency', width: 90, valueGetter: (_v, row) => row.currency.code },
    { field: 'paymentTiming', headerName: 'Timing', width: 140, valueGetter: (_v, row) => row.paymentTiming ?? '—' },
    {
      field: 'isPublished',
      headerName: 'Published',
      width: 100,
      renderCell: (params) => <Chip size="small" label={params.value ? 'Yes' : 'No'} color={params.value ? 'success' : 'default'} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          {canManage && (
            <Button size="small" onClick={() => setEditItem(params.row)}>
              Edit
            </Button>
          )}
          {canManage && (
            <Button
              size="small"
              onClick={() => publishItemMutation.mutate({ itemId: params.row.id, published: !params.row.isPublished })}
            >
              {params.row.isPublished ? 'Unpublish' : 'Publish'}
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        {canCreate && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setAddItemOpen(true)}>
            Add payment item
          </Button>
        )}
      </Stack>
      <Box sx={{ height: 380, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid rows={structure.items} columns={itemColumns} slots={{ toolbar: TableToolbar }} disableRowSelectionOnClick />
      </Box>

      {addItemOpen && (
        <FeeStructureItemDialog
          feeStructureId={structure.id}
          existingCategoryIds={structure.items.map((i) => i.paymentCategoryId)}
          paymentCategories={paymentCategories}
          currencies={currencies}
          onClose={() => setAddItemOpen(false)}
          onError={onError}
        />
      )}
      {editItem && (
        <FeeStructureItemDialog
          feeStructureId={structure.id}
          item={editItem}
          paymentCategories={paymentCategories}
          currencies={currencies}
          onClose={() => setEditItem(null)}
          onError={onError}
        />
      )}
    </>
  );
};

interface FeeStructureItemDialogProps {
  feeStructureId: number;
  item?: FeeStructureItem;
  existingCategoryIds?: number[];
  paymentCategories: PaymentCategoryOption[];
  currencies: CurrencyOption[];
  onClose: () => void;
  onError: (msg: string | null) => void;
}

const FeeStructureItemDialog = ({
  feeStructureId,
  item,
  existingCategoryIds = [],
  paymentCategories,
  currencies,
  onClose,
  onError,
}: FeeStructureItemDialogProps) => {
  const queryClient = useQueryClient();
  const [paymentCategoryId, setPaymentCategoryId] = useState<number | ''>(item?.paymentCategoryId ?? '');
  const [amount, setAmount] = useState(item?.amount ?? '');
  const [firstYearAmount, setFirstYearAmount] = useState(item?.firstYearAmount ?? '');
  const [laterYearAmount, setLaterYearAmount] = useState(item?.laterYearAmount ?? '');
  const [semester1Amount, setSemester1Amount] = useState(item?.semester1Amount ?? '');
  const [semester2Amount, setSemester2Amount] = useState(item?.semester2Amount ?? '');
  const [option1Value, setOption1Value] = useState(item?.option1Value ?? '');
  const [option2Value, setOption2Value] = useState(item?.option2Value ?? '');
  const [currencyId, setCurrencyId] = useState<number | ''>(item?.currency.id ?? '');
  const [isRequired, setIsRequired] = useState(item?.isRequired ?? false);
  const [paymentTiming, setPaymentTiming] = useState(item?.paymentTiming ?? '');

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        paymentCategoryId: paymentCategoryId as number,
        amount: amount === '' ? undefined : Number(amount),
        firstYearAmount: firstYearAmount === '' ? undefined : Number(firstYearAmount),
        laterYearAmount: laterYearAmount === '' ? undefined : Number(laterYearAmount),
        semester1Amount: semester1Amount === '' ? undefined : Number(semester1Amount),
        semester2Amount: semester2Amount === '' ? undefined : Number(semester2Amount),
        option1Value: option1Value === '' ? undefined : option1Value,
        option2Value: option2Value === '' ? undefined : option2Value,
        currencyId: currencyId as number,
        isRequired,
        paymentTiming: paymentTiming || undefined,
      };
      return item ? updateFeeStructureItem(feeStructureId, item.id, payload) : addFeeStructureItem(feeStructureId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-structures'] });
      onError(null);
      onClose();
    },
    onError: (err) => onError(extractErrorMessage(err)),
  });

  const availableCategories = item
    ? paymentCategories
    : paymentCategories.filter((c) => !existingCategoryIds.includes(c.id));

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{item ? 'Edit payment item' : 'Add payment item'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Payment item"
            fullWidth
            disabled={!!item}
            value={paymentCategoryId}
            onChange={(e) => setPaymentCategoryId(Number(e.target.value))}
          >
            {availableCategories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="Amount (one-time)" type="number" fullWidth value={amount} onChange={(e) => setAmount(e.target.value)} />
            <TextField
              select
              label="Currency"
              fullWidth
              value={currencyId}
              onChange={(e) => setCurrencyId(Number(e.target.value))}
            >
              {currencies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.code}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Semester 1 amount"
              type="number"
              fullWidth
              value={semester1Amount}
              onChange={(e) => setSemester1Amount(e.target.value)}
              helperText="Course Fee only"
            />
            <TextField
              label="Semester 2 amount"
              type="number"
              fullWidth
              value={semester2Amount}
              onChange={(e) => setSemester2Amount(e.target.value)}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="1st year amount"
              type="number"
              fullWidth
              value={firstYearAmount}
              onChange={(e) => setFirstYearAmount(e.target.value)}
              helperText="Other expenses only"
            />
            <TextField
              label="Later year amount"
              type="number"
              fullWidth
              value={laterYearAmount}
              onChange={(e) => setLaterYearAmount(e.target.value)}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Option 1 value"
              fullWidth
              placeholder="e.g. 850 USD"
              value={option1Value}
              onChange={(e) => setOption1Value(e.target.value)}
              helperText="Accommodation-style two-option items only"
            />
            <TextField
              label="Option 2 value"
              fullWidth
              placeholder="e.g. 1500-3000 USD"
              value={option2Value}
              onChange={(e) => setOption2Value(e.target.value)}
            />
          </Stack>
          <TextField
            label="Payment timing"
            fullWidth
            placeholder="e.g. Before enrollment, Within 30 days"
            value={paymentTiming}
            onChange={(e) => setPaymentTiming(e.target.value)}
          />
          <FormControlLabel
            control={<Checkbox checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} />}
            label="Required for every student"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!paymentCategoryId || !currencyId || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
